import { useEffect, useState } from "react"
import get from "lodash/get"
import toLower from "lodash/toLower"
import isEmpty from "lodash/isEmpty"
import { wmColor } from "themes-lib/wmTheme"
import { makeStyles } from "tss-react/mui"
import { Box, Typography } from "@mui/material"
import { getWindow } from "utils-lib/getWindow"
import { GenericModal, FormattedTextField } from "component-lib/Base"
import Login from "component-lib/Login/LoginContainer"
import useFeatureFlags from "hooks-lib/LaunchDarkly/useFeatureFlags"
import { isCurrentPageSupportLandingPage } from "utils-lib/isSupportPage"
import { API_STATUSES, lobType, TYPE, lob } from "constants-lib/common"
import { CONTAINER_ISSUE_USECASETYPE } from "constants-lib/bulk"
import { EXCEPTION_MESSAGE_TYPE_CONTAINER } from "constants-lib/exceptionMessageTypesForServlet"
import { SupportOptionsCards } from "../RequestSupport/components/SupportOptionsCards"

const closeIcon = `/content/dam/wm/icons/green-close-x-icon.svg`
const CONTAINER_REPAIR_URL = `/us/en/mywm/user/my-services/container-repair`
const SINGLE_ACCOUNT_DASHBOARD_URL = `/us/en/mywm/user/account-dashboard`
const ssrWindow = getWindow()

const useStyles = makeStyles()((theme) => ({
  buttonClose: {
    width: `100%`,
  },
  description: {
    marginBottom: `24px`,
    [theme.breakpoints.down(`sm`)]: {
      fontSize: "0.875rem",
      lineHeight: "1.375rem",
      marginBottom: `0 !important`,
    },
    "&.miscInstructions": {
      marginBottom: `1rem !important`,
    },
    "& p": {
      marginBottom: "0.5rem",
      "& a": {
        textDecoration: `underline`,
      },
      [theme.breakpoints.down(`sm`)]: {
        marginBottom: "0",
        paddingBottom: `2rem !important`,
      },
    },
  },
  title: {
    fontFamily: `Maax-Black`,
    fontSize: "1.5rem",
    lineHeight: "32px",
    width: `100%`,
    color: theme.palette.common.black,
    [theme.breakpoints.down(`sm`)]: {
      fontSize: `1.25rem`,
      fontFamily: `Maax-Bold`,
      width: `100%`,
    },
  },
  modalClass: {
    minWidth: "30rem",
    [theme.breakpoints.down(`md`)]: {
      minWidth: 0,
    },
    "& .Login": {
      "& .TextInputForm__inputs": {
        paddingLeft: `0 !important`,
        paddingRight: `0 !important`,
      },
      "& .TextInputForm .textFormBulkModal": {
        padding: `0 !important`,
      },
    },
    "& a": {
      color: wmColor.greenHover,
    },
  },
  miscInstructions: {
    display: `flex`,
    flexDirection: `column`,
    minWidth: `25.5rem`,
    [theme.breakpoints.down(`md`)]: {
      minWidth: 0,
    },
  },
  loggedInEligibleModalClass: {
    display: `flex`,
    flexDirection: `column`,
    WebkitBoxAlign: `center`,
    alignItems: `center`,
    backgroundColor: theme.palette.common.white,
    margin: `1rem`,
    padding: `1rem`,
    overflow: `visible`,
    maxWidth: `calc(100% - 31px)`,
    width: `fit-content !important`,
    "& .Login": {
      "& .TextInputForm__inputs": {
        paddingLeft: `0 !important`,
        paddingRight: `0 !important`,
      },
      "& .TextInputForm .textFormBulkModal": {
        padding: `0 !important`,
      },
    },
    "& a": {
      color: wmColor.greenHover,
    },
  },
  buttonWrapper: {
    "& button": {
      lineHeight: `1rem !important`,
      width: `100% !important`,
      borderRadius: `1.75rem !important`,
    },
    [theme.breakpoints.down(`md`)]: {
      minHeight: `auto`,
    },
  },
}))

export const ExceptionMessageContainerModal = ({
  onlineProducts: { servletErrorMessageState, servletErrorMessage = [] },
  customerSupportContact,
  customerSelections,
  siteLanguage,
  setGuidedFlowSelections,
  setCustomerLob,
  getContactBackAttributes,
  getLiveChat,
  shouldDefaultToResi = false,
  cityBilledAnalyticsData = {},
  showModalContainer,
  setShowModalContainer,
  getGuestContainerRepairEligibility,
  getServletErrorMessagesByServiceType,
  getServletFallbackMessage,
  fallbackServletServices,
  isLoggedIn,
}) => {
  const { language } = siteLanguage
  const { eligibilityData, contactBackAttributesStatus } = customerSelections

  const [pageLob, setPageLob] = useState()
  const [hasError, setHasError] = useState(false)
  const [isChannelling, setIsChannelling] = useState(false)
  const [useCaseType, setUseCaseType] = useState(``)
  const [errorObject, setErrorObject] = useState({})
  const [modalDetailsObject, setModalDetailsObject] = useState({})

  const { classes } = useStyles({ useCaseType }[""])
  const {
    LDFFReleaseContainerFlowLoginModal,
    LDFFReleaseHoaRoutingAccountsShouldBeAbleToOrderContainerServicesWithoutLoggingIn,
    LDFFReleaseContainerRepairMalformedUrlFix,
    isLDClientReady,
  } = useFeatureFlags([
    "LDFFReleaseContainerFlowLoginModal",
    "LDFFReleaseHoaRoutingAccountsShouldBeAbleToOrderContainerServicesWithoutLoggingIn",
    "LDFFReleaseContainerRepairMalformedUrlFix",
  ])
  const isLoggedInEligibleUseCase =
    !isLoggedIn && useCaseType === CONTAINER_ISSUE_USECASETYPE.loggedInEligible

  const validUseCaseTypes = Object.values(CONTAINER_ISSUE_USECASETYPE)
  const miscInstructionData = get(
    eligibilityData,
    `response.miscInstructions`,
    ``,
  )

  useEffect(() => {
    if (showModalContainer && !isLDClientReady) {
      return
    }

    if (showModalContainer) {
      if (isLoggedIn && LDFFReleaseContainerFlowLoginModal) {
        setShowModalContainer(false)
        getGuestContainerRepairEligibility(
          null,
          false,
          LDFFReleaseHoaRoutingAccountsShouldBeAbleToOrderContainerServicesWithoutLoggingIn,
          LDFFReleaseContainerRepairMalformedUrlFix,
        )
        return
      }

      if (LDFFReleaseContainerFlowLoginModal) {
        getGuestContainerRepairEligibility(
          null,
          LDFFReleaseContainerFlowLoginModal,
          LDFFReleaseHoaRoutingAccountsShouldBeAbleToOrderContainerServicesWithoutLoggingIn,
          LDFFReleaseContainerRepairMalformedUrlFix,
        )
      } else {
        getGuestContainerRepairEligibility(
          null,
          false,
          LDFFReleaseHoaRoutingAccountsShouldBeAbleToOrderContainerServicesWithoutLoggingIn,
          LDFFReleaseContainerRepairMalformedUrlFix,
        )
      }
    }
  }, [
    showModalContainer,
    LDFFReleaseContainerFlowLoginModal,
    isLDClientReady,
    isLoggedIn,
    LDFFReleaseHoaRoutingAccountsShouldBeAbleToOrderContainerServicesWithoutLoggingIn,
    LDFFReleaseContainerRepairMalformedUrlFix,
  ])

  useEffect(() => {
    if (isEmpty(fallbackServletServices?.getServletFallbackMessageStatus)) {
      getServletFallbackMessage()
    }
    getServletErrorMessagesByServiceType(
      language,
      EXCEPTION_MESSAGE_TYPE_CONTAINER,
    )
    if (servletErrorMessageState === API_STATUSES.FULFILLED) {
      setUseCaseType(
        get(eligibilityData, `response.useCaseType`, ``) ||
          get(eligibilityData, `useCaseType`, ``) ||
          get(eligibilityData, `response.reason`, ``) ||
          get(eligibilityData, `reason`, ``) ||
          (get(eligibilityData, `response.searchFailed`, ``) &&
            CONTAINER_ISSUE_USECASETYPE.defaultExceptionMessageHandling),
      )
    }
  }, [eligibilityData])

  useEffect(() => {
    setErrorObject({})
  }, [customerSelections?.customerAddress])

  useEffect(() => {
    const isServeletFetchedState = servletErrorMessageState

    if (isServeletFetchedState === API_STATUSES.FULFILLED) {
      if (contactBackAttributesStatus !== API_STATUSES.PENDING && useCaseType) {
        getExceptionMessagingObject()
      } else {
        setUseCaseType(
          get(eligibilityData, `response.useCaseType`, ``) ||
            get(eligibilityData, `useCaseType`, ``) ||
            get(eligibilityData, `response.reason`, ``) ||
            get(eligibilityData, `reason`, ``) ||
            (get(eligibilityData, `response.searchFailed`, ``) &&
              CONTAINER_ISSUE_USECASETYPE.defaultExceptionMessageHandling),
        )
      }
    }
  }, [useCaseType, servletErrorMessageState])

  useEffect(() => {
    if (
      useCaseType === CONTAINER_ISSUE_USECASETYPE.commercialROCustomer ||
      useCaseType ===
        CONTAINER_ISSUE_USECASETYPE.defaultExceptionMessageHandling ||
      useCaseType === CONTAINER_ISSUE_USECASETYPE.knowledgeOnly ||
      useCaseType === CONTAINER_ISSUE_USECASETYPE.contactAgent ||
      useCaseType === CONTAINER_ISSUE_USECASETYPE.hoaStatusIneligible
    ) {
      setIsChannelling(true)
    } else {
      setIsChannelling(false)
    }
  }, [useCaseType])

  const callGetContactBackAttributes = async (errorObj) => {
    const customerAddress = customerSelections?.customerAddress
    const postalCode = customerAddress?.postalCode
    if (postalCode) {
      const category =
        errorObj?.category ||
        errorObj?.contactBack?.category ||
        (isCurrentPageSupportLandingPage() &&
          customerSelections.guidedFlowSelections.category)
      const subCategory =
        errorObj?.subcategory ||
        errorObj?.contactBack?.subcategory ||
        (isCurrentPageSupportLandingPage() &&
          customerSelections.guidedFlowSelections.subCategory)
      const correctedSubCategroy = subCategory ? [subCategory] : []

      await getLiveChat(postalCode)

      await setGuidedFlowSelections({
        category,
        subCategory: correctedSubCategroy,
      })

      let lineOfBusiness = ``

      if (
        ssrWindow.location.pathname.includes("bulk-trash-pickup") ||
        shouldDefaultToResi
      ) {
        lineOfBusiness = lobType.RESIDENTIAL
      } else {
        lineOfBusiness = toLower(
          customerSelections?.customerLob ||
            customerSelections?.customerSelectedLob ||
            customerSelections?.checkAvailabilityLob,
        )
        if (
          lineOfBusiness === lob.HOME ||
          lineOfBusiness === toLower(lobType.RESIDENTIAL)
        ) {
          lineOfBusiness = lobType.RESIDENTIAL
        }
        if (
          lineOfBusiness === lob.BUSINESS ||
          lineOfBusiness === toLower(lobType.COMMERCIAL)
        ) {
          lineOfBusiness = lobType.COMMERCIAL
        }
      }

      await getContactBackAttributes({
        category,
        subCategory: correctedSubCategroy,
        lineOfBusiness,
        customerAddress,
      })

      setPageLob(lineOfBusiness)
    }
  }

  const getExceptionMessagingObject = async () => {
    let modalDetails = {
      hideCTAButtons: true,
    }
    const isEligible =
      get(eligibilityData, `response.isEligible`, ``) ||
      get(eligibilityData, `isEligible`, ``)

    const servletErrorObject =
      servletErrorMessage.find(
        (el) =>
          el.reason === useCaseType &&
          (!isEligible || !el?.isEligible || el.isEligible === isEligible),
      ) || servletErrorMessage.find((el) => el.reason === useCaseType)

    setErrorObject(servletErrorObject)

    if (LDFFReleaseContainerFlowLoginModal && isLoggedInEligibleUseCase) {
      modalDetails = {
        hideCTAButton: true,
      }
    } else if (
      useCaseType === CONTAINER_ISSUE_USECASETYPE.commercialROCustomer ||
      useCaseType ===
        CONTAINER_ISSUE_USECASETYPE.defaultExceptionMessageHandling ||
      useCaseType === CONTAINER_ISSUE_USECASETYPE.knowledgeOnly ||
      useCaseType === CONTAINER_ISSUE_USECASETYPE.contactAgent ||
      useCaseType === CONTAINER_ISSUE_USECASETYPE.hoaStatusIneligible
    ) {
      await callGetContactBackAttributes(servletErrorObject)
    } else {
      modalDetails = {
        CTAPrimaryText: servletErrorObject?.ctaText1,
        hideCTAButton: false,
        onPrimaryButtonClick: () => setShowModalContainer(false),
        buttonId: "ContainerErrorModal-HasMiscInstructions",
      }
    }
    setModalDetailsObject(modalDetails)
  }

  const renderLoggedInEligibleHeading = () => (
    <>
      <Typography variant="h4" className={`${classes.title} mb-2`}>
        {errorObject?.heading}
      </Typography>
      {errorObject?.subHeading && (
        <FormattedTextField
          useTypography
          component="p"
          variant="body1"
          className={classes.description}
        >
          {LDFFReleaseContainerFlowLoginModal
            ? errorObject?.subHeading?.replace(
                /\/user\/register\.html/gi,
                `/user/register`,
              )
            : errorObject?.subHeading}
        </FormattedTextField>
      )}
      {(errorObject?.exceptionMessage?.html ||
        errorObject?.exceptionMessage) && (
        <FormattedTextField
          useTypography
          component="p"
          variant="body1"
          className={classes.description}
        >
          {LDFFReleaseContainerFlowLoginModal
            ? errorObject?.exceptionMessage?.html?.replace(
                /\/user\/register\.html/gi,
                `/user/register`,
              )
            : errorObject?.exceptionMessage?.html}
        </FormattedTextField>
      )}
    </>
  )

  const renderWrapperWithHeading = (content) => (
    <Box data-testid="ContainerLoggedInEligibleContent">
      {renderLoggedInEligibleHeading()}
      {content}
    </Box>
  )

  const renderLogin = () =>
    renderWrapperWithHeading(
      <Login
        emailPlaceholderText={errorObject?.label3}
        loginButtonCta={errorObject?.ctaText1}
        forgotEmailPwText={errorObject?.ctaText2}
        passwordPlaceholderText={errorObject?.label4}
        redirectUrl={
          LDFFReleaseContainerRepairMalformedUrlFix
            ? SINGLE_ACCOUNT_DASHBOARD_URL
            : CONTAINER_REPAIR_URL
        }
        siteLanguage={siteLanguage}
        hideSignUp
        hideContinueAsGuest
      />,
    )

  const renderMiscInstructions = () => (
    <Box
      className={classes.miscInstructions}
      data-testid="ContainerErrorModal-HasMiscInstructions"
    >
      <Typography variant="h4" className={`${classes.title} mb-2`}>
        {CONTAINER_ISSUE_USECASETYPE.contactCity === useCaseType
          ? miscInstructionData
          : errorObject?.heading}
      </Typography>
      <FormattedTextField
        useTypography
        component="p"
        variant="body1"
        className={`miscInstructions ${classes.description}`}
      >
        {errorObject?.subHeading}
      </FormattedTextField>
      {(errorObject?.exceptionMessage?.html ||
        errorObject?.exceptionMessage) && (
        <FormattedTextField>
          {errorObject?.exceptionMessage?.html}
        </FormattedTextField>
      )}
    </Box>
  )

  const renderModal = () => {
    if (
      LDFFReleaseContainerFlowLoginModal &&
      isLoggedIn &&
      useCaseType === CONTAINER_ISSUE_USECASETYPE.loggedInEligible
    ) {
      return null
    }

    if (
      isEmpty(errorObject) &&
      !(LDFFReleaseContainerFlowLoginModal && isLoggedInEligibleUseCase)
    ) {
      return null
    }

    const { hideCTAButton, CTAPrimaryText, onPrimaryButtonClick, buttonId } =
      modalDetailsObject

    return (
      <GenericModal
        open={showModalContainer}
        onClose={() => {
          setShowModalContainer(false)
          setErrorObject({})
        }}
        showCloseButton
        closeButtonIcon={closeIcon}
        hideButtons={hideCTAButton}
        hideErrorMessage
        primaryCTA={CTAPrimaryText}
        primaryOnClick={onPrimaryButtonClick}
        primaryTestingId={buttonId}
        useFlexibleWidth={isChannelling}
        customClass={
          LDFFReleaseContainerFlowLoginModal && isLoggedInEligibleUseCase
            ? classes.loggedInEligibleModalClass
            : classes.modalClass
        }
        customButtonContainerClass={classes.buttonWrapper}
        modalTitle={errorObject?.heading}
        modalDescription={errorObject?.subHeading}
      >
        <Box data-testid="ContainerErrorModal">
          {LDFFReleaseContainerFlowLoginModal && isLoggedInEligibleUseCase ? (
            renderLogin()
          ) : !isChannelling ? (
            renderMiscInstructions()
          ) : (
            <SupportOptionsCards
              topSupportTopicData={{}}
              contactOptions={{
                options: [
                  {
                    type: TYPE.LIVECHAT,
                    ctaText: errorObject.ctaText1,
                    icon: errorObject.ctaUrl1,
                    optionsbody: errorObject.ctaBody1,
                    optionsHeader: errorObject.ctaHeader1,
                    optionsAlternateHeader: errorObject.ctaAlt1,
                  },
                  {
                    type: TYPE.PHONE,
                    ctaText: errorObject.ctaText2,
                    icon: errorObject.ctaUrl2,
                    optionsbody: errorObject.ctaBody2,
                    optionsHeader: errorObject.ctaHeader2,
                    optionsAlternateHeader: errorObject.ctaAlt2,
                  },
                  {
                    type: TYPE.CALLBACK,
                    ctaText: errorObject.ctaText3,
                    icon: errorObject.ctaUrl3,
                    optionsbody: errorObject.ctaBody3,
                    optionsHeader: errorObject.ctaHeader3,
                    optionsAlternateHeader: errorObject.ctaAlt3,
                  },
                  {
                    type: TYPE.EMAIL,
                    ctaText: errorObject.ctaText4,
                    icon: errorObject.ctaUrl4,
                    optionsbody: errorObject.ctaBody4,
                    optionsHeader: errorObject.ctaHeader4,
                    optionsAlternateHeader: errorObject.ctaAlt4,
                  },
                ],
              }}
              customerSelections={customerSelections}
              customerSupportContact={customerSupportContact}
              language={language}
              getLiveChat={getLiveChat}
              hasError={hasError}
              setHasError={setHasError}
              contactBackUrl={
                errorObject?.contactBackUrl ||
                errorObject?.contactBack?.contactBackUrl
              }
              confirmationIcon={
                errorObject?.confirmationIcon ||
                errorObject?.contactBack?.confirmationIcon
              }
              redirectMessage={
                errorObject?.redirectMessage ||
                errorObject?.contactBack?.redirectMessage
              }
              handleCloseModal={() => setShowModalContainer(false)}
              isLobHome={pageLob === lobType.RESIDENTIAL}
              setIsLobHome={() => setPageLob(lobType.RESIDENTIAL)}
              isLobBusiness={pageLob === lobType.COMMERCIAL}
              setIsLobBusiness={() => setPageLob(lobType.COMMERCIAL)}
              setCustomerLob={setCustomerLob}
              pageLob={pageLob}
              hidePhoneForHomeLOB={
                errorObject?.hidePhoneForHomeLOB ||
                errorObject?.contactBack?.hidePhoneForHomeLOB ||
                false
              }
              hidePhoneForBusinessLOB={
                errorObject?.hidePhoneForBusinessLOB ||
                errorObject?.contactBack?.hidePhoneForBusinessLOB ||
                false
              }
              unavailableServiceText={
                fallbackServletServices?.servletFallbackMessage
                  ?.unavailableServiceText?.html
              }
              unavailableServiceHeader={
                fallbackServletServices?.servletFallbackMessage
                  ?.unavailableServiceHeader
              }
              renderParentHeader={renderMiscInstructions}
              bypassSupportServlet
              cityBilledAnalyticsData={cityBilledAnalyticsData}
            />
          )}
        </Box>
      </GenericModal>
    )
  }

  return (
    <div data-testid="Container-Issue">
      {validUseCaseTypes.includes(useCaseType) && renderModal()}
    </div>
  )
}

export default ExceptionMessageContainerModal

// Note: React 19 no-longer supports propTypes. Leaving this for reference.
// ExceptionMessageContainerModal.propTypes = {
//   onlineProducts: PropTypes.object.isRequired,
//   customerSupportContact: PropTypes.object.isRequired,
//   customerSelections: PropTypes.object.isRequired,
//   siteLanguage: PropTypes.object.isRequired,
//   setGuidedFlowSelections: PropTypes.func.isRequired,
//   setCustomerLob: PropTypes.func.isRequired,
//   getContactBackAttributes: PropTypes.func.isRequired,
//   getLiveChat: PropTypes.func.isRequired,
//   shouldDefaultToResi: PropTypes.bool,
//   cityBilledAnalyticsData: PropTypes.object,
//   showModalContainer: PropTypes.bool.isRequired,
//   setShowModalContainer: PropTypes.bool.isRequired,
//   getGuestContainerRepairEligibility: PropTypes.func.isRequired,
//   getContainerServletErrorMessages: PropTypes.func.isRequired,
// }

// Note: React 19 no-longer supports defaultProps. Leaving this for reference.
// ExceptionMessageContainerModal.defaultProps = {
//   shouldDefaultToResi: false,
//   cityBilledAnalyticsData: {},
// }
